import React from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { NotificationItem } from './NotificationItem';
import type { Notification } from '../../../types/types';
import { useNavigate } from 'react-router-dom';

interface NotificationListProps {
  notifications: Notification[];
  loading?: boolean;
  error?: string | null;
  onMarkAsRead?: (id: string) => void;
  onMarkAllAsRead?: () => void;
  onDelete?: (id: string) => void;
  onViewAll?: () => void;
  maxItems?: number;
  compact?: boolean;
  showActions?: boolean;
}

export const NotificationList: React.FC<NotificationListProps> = ({
  notifications,
  loading = false,
  error = null,
  onMarkAsRead,
  onMarkAllAsRead,
  onDelete,
  onViewAll,
  maxItems,
  compact = false,
  showActions = true,
}) => {
  const navigate = useNavigate();

  const displayedNotifications = maxItems
    ? notifications.slice(0, maxItems)
    : notifications;

  const hasUnread = notifications.some((n) => !n.read);

  const handleNotificationClick = (notification: Notification) => {
    if (notification.link) {
      navigate(notification.link);
    }
  };

  if (loading && notifications.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (notifications.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          px: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No notifications yet
        </Typography>
        <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ mt: 0.5 }}>
          You&apos;ll receive notifications for important events
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Actions Header */}
      {showActions && hasUnread && onMarkAllAsRead && (
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Button
            size="small"
            variant="text"
            onClick={onMarkAllAsRead}
            sx={{ fontSize: '0.75rem', textTransform: 'none' }}
          >
            Mark all as read
          </Button>
        </Box>
      )}

      {/* Notification Items */}
      <Box>
        {displayedNotifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onMarkAsRead={onMarkAsRead}
            onDelete={onDelete}
            onClick={handleNotificationClick}
            compact={compact}
          />
        ))}
      </Box>

      {/* View All Footer */}
      {onViewAll && maxItems && notifications.length > maxItems && (
        <>
          <Divider />
          <Box sx={{ p: 1.5, textAlign: 'center' }}>
            <Button
              fullWidth
              size="small"
              variant="text"
              onClick={onViewAll}
              sx={{ fontSize: '0.8rem', textTransform: 'none' }}
            >
              View All Notifications ({notifications.length})
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default NotificationList;
